import { useState, useEffect } from 'react';
import { Rocket, CheckCircle2, XCircle, Ban, X, ChevronUp } from 'lucide-react';
import { useDeployAll } from '../contexts/DeployAllContext';
import { summarize, durationText, hasInFlight } from '../utils/deployAllOrchestration';

import { RubyGemMark } from './RubyGemMark';
/**
 * DeployAllWidget — floating pill shown when a Deploy All batch is active but the
 * full modal is minimized. Click to reopen. Lives at app root so it survives
 * route changes and (via the provider's localStorage) full page refreshes.
 */
export function DeployAllWidget() {
  const { batch, isModalOpen, expand, discard } = useDeployAll();

  // Local 1s tick for the elapsed timer (only while the widget is showing an active batch).
  const ticking = !!batch && !isModalOpen && (batch.status === 'running' || hasInFlight(batch.progress));
  const [, setElapsedTick] = useState(0);
  useEffect(() => {
    if (!ticking) return;
    const id = setInterval(() => setElapsedTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [ticking]);

  if (!batch || isModalOpen || batch.status === 'preview') return null;

  const { environment, product, status, currentPhaseIdx, plan, progress, batchStartedAt } = batch;
  const summary = summarize(progress);
  const totalPhases = plan?.phases.length || 0;
  const phaseNo = currentPhaseIdx >= 0 ? currentPhaseIdx + 1 : 0;
  const elapsed = batchStartedAt ? durationText(batchStartedAt) : '';

  const isRunning = status === 'running' || summary.running > 0;
  const accent = status === 'failed' ? 'border-red-500/50'
    : status === 'done' ? 'border-emerald-500/50'
    : isRunning ? 'border-blue-500/50'
    : 'border-dark-600';

  return (
    <div className={`fixed bottom-4 right-4 z-40 bg-dark-900 border ${accent} rounded-xl shadow-2xl w-72 overflow-hidden`}>
      <button
        onClick={expand}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-dark-800 text-left"
        title="Reopen deploy progress"
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${environment === 'prod' ? 'bg-red-500/20' : 'bg-emerald-500/20'}`}>
          {status === 'failed' ? <XCircle size={16} className="text-red-400" />
            : status === 'done' ? <CheckCircle2 size={16} className="text-emerald-400" />
            : isRunning ? <RubyGemMark size={16} drawing />
            : <Rocket size={16} className={environment === 'prod' ? 'text-red-400' : 'text-emerald-400'} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-white text-sm font-medium truncate">
              Deploy {environment === 'prod' ? 'Prod' : 'Dev'}
            </span>
            <span className="text-[10px] text-gray-500">{product}</span>
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {status === 'done' ? 'Complete'
              : status === 'failed' ? `Phase ${plan?.phases[currentPhaseIdx]?.phase} halted`
              : status === 'aborted' ? 'Aborted'
              : totalPhases > 0 ? `Phase ${phaseNo}/${totalPhases}` : 'Starting…'}
            {elapsed && ` · ${elapsed}`}
          </div>
        </div>
        <ChevronUp size={16} className="text-gray-500 shrink-0" />
      </button>

      {/* Counts strip */}
      <div className="flex items-center gap-3 px-4 py-2 border-t border-dark-700 bg-dark-850 text-xs">
        <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 size={12} />{summary.success}</span>
        {summary.running > 0 && <span className="flex items-center gap-1 text-blue-400"><RubyGemMark size={12} drawing color="currentColor" />{summary.running}</span>}
        {summary.failure > 0 && <span className="flex items-center gap-1 text-red-400"><XCircle size={12} />{summary.failure}</span>}
        {summary.cancelled > 0 && <span className="flex items-center gap-1 text-orange-400"><Ban size={12} />{summary.cancelled}</span>}
        {summary.pending > 0 && <span className="text-gray-500">{summary.pending} pending</span>}
        {(status === 'done' || status === 'failed' || status === 'aborted') && (
          <button
            onClick={(e) => { e.stopPropagation(); discard(); }}
            className="ml-auto text-gray-500 hover:text-white p-0.5 rounded hover:bg-dark-700"
            title="Dismiss"
          >
            <X size={13} />
          </button>
        )}
      </div>
    </div>
  );
}
